import type { FunctionComponent } from 'react';
import { useMemo, useCallback } from 'react';

import { ContextNavLink } from '../../../shared/components/context-nav-link/context-nav-link';
import { FormattedMessage } from '../../../shared/components/formatted-message/formatted-message';
import { useFullPath } from '../../../shared/hooks/use-full-location';
import { createClassName, createStyleHelper } from '../../../shared/utils/class-names';

import styles from './user-menu.module.scss';

const style = createStyleHelper(styles, 'user-menu');

export interface IUserMenuProps {
    className?: string;
    onClose?: () => void;
}

export const UserMenu: FunctionComponent<IUserMenuProps> = ({ className, onClose }) => {
    const returnPath = useFullPath();
    const menuSections = useMemo(
        () => [
            {
                id: 'overview',
                translationKey: 'top-navigation.user-info.overview',
                defaultDisplayName: 'Overview',
                href: '/account/overview',
            },
            {
                id: 'rewards',
                translationKey: 'top-navigation.user-info.rewards',
                defaultDisplayName: 'Rewards',
                href: '/account/rewards',
            },
            {
                id: 'profile',
                translationKey: 'top-navigation.user-info.profile',
                defaultDisplayName: 'My Profile',
                href: '/account/profile',
            },
            {
                id: 'settings',
                translationKey: 'top-navigation.user-info.settings',
                defaultDisplayName: 'Settings',
                href: '/account/settings',
            },
            {
                id: 'transactions',
                translationKey: 'top-navigation.user-info.transactions',
                defaultDisplayName: 'Transactions',
                href: '/account/transactions',
            },
            {
                id: 'play-safe',
                translationKey: 'top-navigation.user-info.play-safe',
                defaultDisplayName: 'Play Safe',
                href: '/account/responsible-gaming/sports-budget-limits',
            },
            {
                id: 'logout',
                translationKey: 'top-navigation.user-info.logout',
                defaultDisplayName: 'Logout',
                href: `/logout?backTo=${returnPath}`,
            },
        ],
        [returnPath],
    );

    const classList = useCallback(({ isActive }: { isActive: boolean }) => {
        return style('link', { active: isActive });
    }, []);

    return (
        <div className={createClassName([className, style()])}>
            <nav className={style('wrapper')}>
                {menuSections.map((item) => {
                    return (
                        <ContextNavLink
                            onClick={onClose}
                            className={classList}
                            key={item.id}
                            to={item.href}
                        >
                            <FormattedMessage
                                id={item.translationKey}
                                defaultMessage={item.defaultDisplayName}
                            />
                        </ContextNavLink>
                    );
                })}
            </nav>
        </div>
    );
};
